/**
 * CanalJornada V55 — Lista expandida de clientes com saúde, adesão, drill-down
 */
import SalviaLayout from "@/components/SalviaLayout";
import { useAuth } from "@/_core/hooks/useAuth";
import { trpc } from "@/lib/trpc";
import { usePageTitle } from "@/hooks/usePageTitle";
import { StageBadge, AdesaoBar } from "@/components/dashboard";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { AlertTriangle, ChevronRight, Search, Heart } from "lucide-react";
import { Link } from "wouter";
import { useState, useMemo } from "react";
import { GaugeChart, InfoTooltip } from "@/components/home";

export default function CanalJornada() {
  usePageTitle("Jornada dos Clientes");
  const { user } = useAuth();
  const isAllowed = user?.role === "admin" || user?.role === "channel_admin" || user?.role === "channel_admin_multi" || user?.role === "channel_operator" || user?.role === "channel_consultant";
  const [search, setSearch] = useState("");
  const [stageFilter, setStageFilter] = useState<string>("all");

  const { data, isLoading, error } = trpc.dashboardsAdv.channel.journeyList.useQuery(undefined, {
    enabled: !!user && isAllowed,
    refetchOnWindowFocus: false,
  });

  const filtered = useMemo(() => {
    if (!data?.clients) return [];
    return data.clients.filter((c: any) => {
      const matchSearch = !search || c.companyName?.toLowerCase().includes(search.toLowerCase()) || c.cnpj?.includes(search);
      const matchStage = stageFilter === "all" || c.stage === stageFilter;
      return matchSearch && matchStage;
    });
  }, [data?.clients, search, stageFilter]);

  if (error) {
    return (
      <SalviaLayout title="Dashboard — Jornada dos Clientes">
        <Card className="p-8 text-center">
          <AlertTriangle className="mx-auto mb-4 text-destructive" size={48} />
          <p className="text-muted-foreground">{error.message}</p>
        </Card>
      </SalviaLayout>
    );
  }

  const loading = isLoading || !data;

  const saudeColor = (v: number) => v >= 70 ? "text-emerald-600" : v >= 40 ? "text-amber-600" : "text-red-600";

  return (
    <SalviaLayout title="Dashboard — Jornada dos Clientes">
      <div className="space-y-6">
        {/* Stage Summary */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {(data?.stageSummary ?? []).map((s: any) => (
            <Card
              key={s.stage}
              className={`p-4 text-center cursor-pointer transition-all ${stageFilter === s.stage ? "ring-2 ring-[#002F59]" : "hover:bg-muted/30"}`}
              onClick={() => setStageFilter(stageFilter === s.stage ? "all" : s.stage)}
            >
              <div className="text-2xl font-bold tabular-nums">{s.count}</div>
              <div className="mt-1"><StageBadge stage={s.stage} /></div>
            </Card>
          ))}
        </div>

        {/* Search + Info */}
        <div className="flex items-center gap-4">
          <div className="relative flex-1 max-w-sm">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              placeholder="Buscar por nome ou CNPJ..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#002F59]/30"
            />
          </div>
          <span className="text-xs text-muted-foreground">{filtered.length} de {data?.totalClients ?? 0} clientes</span>
          <InfoTooltip label="Saúde da Conta" formula="Saúde = (adesão×0.30) + (progresso×0.30) + (taxaDoc×0.20) + ((1-bloqueio)×0.20)" description="Índice composto de 0 a 100" />
        </div>

        {/* Client Table */}
        <Card className="p-6">
          {loading ? (
            <div className="space-y-3">{[1,2,3,4,5].map(i => <Skeleton key={i} className="h-10 w-full" />)}</div>
          ) : filtered.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">Nenhum cliente encontrado</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="px-3 py-3 text-left text-xs font-semibold text-muted-foreground uppercase">Empresa</th>
                    <th className="px-3 py-3 text-left text-xs font-semibold text-muted-foreground uppercase">Etapa</th>
                    <th className="px-3 py-3 text-right text-xs font-semibold text-muted-foreground uppercase">Vidas</th>
                    <th className="px-3 py-3 text-right text-xs font-semibold text-muted-foreground uppercase">Adesão</th>
                    <th className="px-3 py-3 text-left text-xs font-semibold text-muted-foreground uppercase w-28">Progresso</th>
                    <th className="px-3 py-3 text-center text-xs font-semibold text-muted-foreground uppercase">Saúde</th>
                    <th className="px-3 py-3 text-right text-xs font-semibold text-muted-foreground uppercase">Docs</th>
                    <th className="px-3 py-3 text-center text-xs font-semibold text-muted-foreground uppercase w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((c: any) => (
                    <tr key={c.companyId} className="border-b last:border-0 hover:bg-muted/30">
                      <td className="px-3 py-2.5">
                        <div className="font-medium">{c.companyName}</div>
                        {c.estado && <div className="text-xs text-muted-foreground">{c.estado}</div>}
                      </td>
                      <td className="px-3 py-2.5"><StageBadge stage={c.stage} /></td>
                      <td className="px-3 py-2.5 text-right tabular-nums">{c.totalVidas.toLocaleString("pt-BR")}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums">{c.adesao}%</td>
                      <td className="px-3 py-2.5"><AdesaoBar completed={c.concluidas} total={c.totalPendings} /></td>
                      <td className="px-3 py-2.5 text-center">
                        <div className="flex items-center justify-center gap-1">
                          <Heart size={14} className={saudeColor(c.saude)} />
                          <span className={`font-semibold tabular-nums ${saudeColor(c.saude)}`}>{c.saude}</span>
                        </div>
                      </td>
                      <td className="px-3 py-2.5 text-right tabular-nums">{c.docsReady}</td>
                      <td className="px-3 py-2.5 text-center">
                        <Link href={`/dashboards/canal/jornada/${c.companyId}`}>
                          <ChevronRight size={16} className="text-muted-foreground hover:text-foreground cursor-pointer" />
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </SalviaLayout>
  );
}
